import { useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useIsDesktop } from '@/features/discovery/hooks/use-is-desktop'

/** Bottom-navigation order. `/activities` is the discovery alias of Home. */
export const SWIPE_TAB_PATHS = [
  '/home',
  '/saved',
  '/create',
  '/chats',
  '/profile',
] as const

/**
 * Horizontal rails and map canvases own their own horizontal gestures, and
 * form controls must keep text selection/cursor movement native.
 */
const SWIPE_IGNORE_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[role="dialog"]',
  '[aria-modal="true"]',
  '.maplibregl-map',
  '.map-canvas',
  '.category-rail',
  '.bottom-nav',
  '.desktop-rail',
  '[data-swipe-ignore]',
].join(',')

const SLIDE_TRANSITION =
  'transform 210ms cubic-bezier(0.22, 1, 0.36, 1), opacity 160ms ease'

function tabIndexFor(pathname: string): number {
  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  const path = normalized === '/activities' ? '/home' : normalized
  return SWIPE_TAB_PATHS.indexOf(path as (typeof SWIPE_TAB_PATHS)[number])
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/**
 * Mobile-only swipe navigation between the five shell tabs (D-058).
 *
 * The gesture follows the finger on the X axis. It never wraps: Home swiped
 * right and Profile swiped left only expose a short elastic resistance. On a
 * committed swipe the current view slides out, the route changes and the next
 * view slides in from the opposite side. Desktop and non-tab routes are
 * untouched; dialogs, maps, horizontal rails and form controls opt out.
 */
export function useSwipeTabs() {
  const mainRef = useRef<HTMLElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const pendingEnter = useRef(0)
  const previousIndexRef = useRef(-1)
  const navTimer = useRef<number | null>(null)
  const suppressClickUntil = useRef(0)
  const indexRef = useRef(-1)
  const index = tabIndexFor(pathname)

  // Keep the gesture handlers on the tab for the latest committed route even
  // if an old listener fires before React re-attaches it.
  useLayoutEffect(() => {
    indexRef.current = index
  }, [index])

  /**
   * The new route is painted while the stage is still off-screen, then a frame
   * later it slides to rest. Styles are changed imperatively before the paint
   * so the destination never flashes at its final position.
   */
  useLayoutEffect(() => {
    const stage = stageRef.current
    if (!stage) return

    const requested = pendingEnter.current
    pendingEnter.current = 0
    const previousIndex = previousIndexRef.current
    previousIndexRef.current = index

    if (requested === 0 || prefersReducedMotion()) {
      stage.style.transition = 'none'
      stage.style.transform = ''
      stage.style.opacity = ''
      return
    }

    // Derive the entrance side from the actual tab order, not only from the
    // gesture. Android WebViews have produced a stale/incorrect gesture sign
    // after a route change; the route index is authoritative.
    const direction =
      index > previousIndex ? 1 : index < previousIndex ? -1 : requested
    const distance = Math.min(window.innerWidth * 0.34, 280)
    stage.style.transition = 'none'
    // Next tabs (+1) enter from the right; previous tabs (-1) from the left,
    // mirroring the outgoing slide.
    stage.style.transform = `translate3d(${direction * distance}px, 0, 0)`
    stage.style.opacity = '0.35'
    // Force the start position to be committed before the transition starts.
    // Android WebView otherwise sometimes paints the previous transform first.
    void stage.offsetWidth

    const frame = window.requestAnimationFrame(() => {
      stage.style.transition = SLIDE_TRANSITION
      stage.style.transform = ''
      stage.style.opacity = ''
    })

    return () => window.cancelAnimationFrame(frame)
  }, [index, pathname])

  useLayoutEffect(() => {
    const main = mainRef.current
    const stage = stageRef.current
    if (!main || !stage) return

    // TypeScript keeps the guard's narrowing in a plain alias inside the
    // event callbacks below.
    const stageElement: HTMLDivElement = stage

    if (isDesktop) {
      stageElement.style.transition = 'none'
      stageElement.style.transform = ''
      stageElement.style.opacity = ''
      return
    }

    // Only the five tab roots participate. Detail, chat, legal and admin
    // routes keep their own navigation model.
    if (index < 0) return

    let startX = 0
    let startY = 0
    let axis: 'pending' | 'horizontal' | 'vertical' = 'pending'
    let dragX = 0
    // A gesture that started on an opted-out surface (map canvas, rail, form
    // field) must be ignored for its whole lifetime. Returning early from
    // touchstart without this flag let the following touchmove treat the
    // ignored gesture as a swipe with stale coordinates.
    let ignored = false

    function settle() {
      stageElement.style.transition = prefersReducedMotion() ? 'none' : SLIDE_TRANSITION
      stageElement.style.transform = ''
      stageElement.style.opacity = ''
    }

    function commit(nextIndex: number, direction: number) {
      pendingEnter.current = direction

      if (prefersReducedMotion()) {
        stageElement.style.transition = 'none'
        stageElement.style.transform = ''
        stageElement.style.opacity = ''
        navigate(SWIPE_TAB_PATHS[nextIndex])
        return
      }

      // `direction` is +1 for the next tab (finger moved left) and -1 for the
      // previous one. The outgoing page continues toward the finger, so the
      // sign is inverted relative to the incoming offset below.
      const out = -direction * Math.min(window.innerWidth * 0.55, 420)
      stageElement.style.transition = SLIDE_TRANSITION
      stageElement.style.transform = `translate3d(${out}px, 0, 0)`
      stageElement.style.opacity = '0'

      navTimer.current = window.setTimeout(() => {
        navTimer.current = null
        navigate(SWIPE_TAB_PATHS[nextIndex])
      }, 140)
    }

    function onTouchStart(event: TouchEvent) {
      ignored = false
      axis = 'pending'
      dragX = 0
      if (pendingEnter.current !== 0) {
        ignored = true
        return
      }
      if (event.touches.length !== 1) {
        ignored = true
        return
      }
      // Swiping behind an open modal would drag its fixed scrim with the page.
      if (document.querySelector('[aria-modal="true"]')) {
        ignored = true
        return
      }
      const target = event.target
      if (
        !(target instanceof Element) ||
        target.closest(SWIPE_IGNORE_SELECTOR)
      ) {
        ignored = true
        return
      }
      const touch = event.touches[0]
      startX = touch.clientX
      startY = touch.clientY
    }

    function onTouchMove(event: TouchEvent) {
      if (ignored) return
      if (axis === 'vertical') return
      const touch = event.touches[0]
      if (!touch) return
      const dx = touch.clientX - startX
      const dy = touch.clientY - startY

      if (axis === 'pending') {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return
        axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'horizontal' : 'vertical'
        if (axis === 'vertical') return
        stageElement.style.transition = 'none'
        suppressClickUntil.current = window.performance.now() + 400
      }

      if (event.cancelable) event.preventDefault()

      const direction = dx < 0 ? 1 : -1
      const baseIndex = indexRef.current
      if (baseIndex < 0) return
      const nextIndex = baseIndex + direction
      const allowed =
        nextIndex >= 0 && nextIndex < SWIPE_TAB_PATHS.length
      const limit = Math.min(window.innerWidth * 0.42, 320)
      const magnitude =
        Math.min(Math.abs(dx), limit) +
        Math.max(0, Math.abs(dx) - limit) * 0.18

      dragX = allowed
        ? Math.sign(dx) * magnitude
        : clamp(dx * 0.18, -36, 36)
      stageElement.style.transform = `translate3d(${dragX}px, 0, 0)`
    }

    function onTouchEnd() {
      if (ignored) {
        ignored = false
        axis = 'pending'
        dragX = 0
        return
      }
      if (axis !== 'horizontal') {
        axis = 'pending'
        return
      }

      const direction = dragX < 0 ? 1 : -1
      const baseIndex = indexRef.current
      if (baseIndex < 0) return
      const nextIndex = baseIndex + direction
      const allowed =
        nextIndex >= 0 && nextIndex < SWIPE_TAB_PATHS.length
      const threshold = Math.max(56, window.innerWidth * 0.16)
      const crossed = allowed && Math.abs(dragX) >= threshold

      axis = 'pending'
      dragX = 0
      if (crossed) commit(nextIndex, nextIndex > baseIndex ? 1 : -1)
      else settle()
    }

    function onTouchCancel() {
      if (axis === 'horizontal') settle()
      ignored = false
      axis = 'pending'
      dragX = 0
    }

    function onClick(event: MouseEvent) {
      if (window.performance.now() < suppressClickUntil.current) {
        suppressClickUntil.current = 0
        event.preventDefault()
        event.stopPropagation()
      }
    }

    main.addEventListener('touchstart', onTouchStart, { passive: true })
    main.addEventListener('touchmove', onTouchMove, { passive: false })
    main.addEventListener('touchend', onTouchEnd)
    main.addEventListener('touchcancel', onTouchCancel)
    main.addEventListener('click', onClick, true)

    return () => {
      if (navTimer.current !== null) {
        window.clearTimeout(navTimer.current)
        navTimer.current = null
      }
      main.removeEventListener('touchstart', onTouchStart)
      main.removeEventListener('touchmove', onTouchMove)
      main.removeEventListener('touchend', onTouchEnd)
      main.removeEventListener('touchcancel', onTouchCancel)
      main.removeEventListener('click', onClick, true)
    }
  }, [index, isDesktop, navigate, pathname])

  return { mainRef, stageRef }
}
